// All notification types from backend NotificationType enum
export type NotificationType =
  // User
  | 'TRANSACTION_RECEIVED'
  | 'TRANSACTION_SENT'
  | 'KYC_APPROVED'
  | 'KYC_REJECTED'
  | 'SECURITY_ALERT'
  | 'LOGIN_NEW_DEVICE'
  | 'LOW_BALANCE'
  | 'WELCOME'
  | 'PAYMENT_LINK_PAID'
  | 'INVOICE_PAID'
  | 'BILL_INQUIRY'
  | 'BILL_PAYMENT_SUCCESS'
  | 'BILL_PAYMENT_FAILED'
  | 'BILL_DUE_REMINDER'
  | 'BILL_OVERDUE'
  | 'CASH_IN_COMPLETED'
  | 'CASH_OUT_INITIATED'
  | 'CASH_OUT_COMPLETED'
  // Agent
  | 'AGENT_REGISTERED'
  | 'AGENT_APPROVED'
  | 'AGENT_REJECTED'
  | 'AGENT_SUSPENDED'
  | 'AGENT_ACTIVATED'
  | 'COMMISSION_EARNED'
  | 'COMMISSIONS_SETTLED'
  | 'FLOAT_LOW'
  | 'FLOAT_CRITICAL'
  | 'FLOAT_DEPLETED'
  | 'FLOAT_REPLENISHED'
  | 'FLOAT_RECHARGE_REQUESTED'
  | 'FLOAT_RECHARGE_REJECTED'
  | 'DAILY_REPORT_GENERATED'
  // Business
  | 'SETTLEMENT_CREATED'
  | 'SETTLEMENT_COMPLETED'
  | 'SETTLEMENT_FAILED'
  // System
  | 'SYSTEM_MAINTENANCE'
  | 'SYSTEM_UPDATE'
  // Float recharge (WS legacy keys from float-recharge api)
  | 'float:recharge_approved'
  | 'float:recharge_rejected';

export interface AppNotification {
  notificationId: string;
  type: NotificationType;
  title: string;
  message: string;
  data?: Record<string, unknown>;
  createdAt: string;
  timestamp?: string;
  read: boolean;
}

// Wallet balance updated event
export interface WalletBalanceUpdatedEvent {
  walletId: string;
  userId: string;
  availableBalance: number;
  totalBalance: number;
  currency: string;
  timestamp: string;
}

// Transaction events
export interface TransactionReceivedEvent {
  transactionId: string;
  reference: string;
  amount: number;
  fee: number;
  totalAmount: number;
  currency: string;
  senderName: string;
  description?: string;
  createdAt: string;
  timestamp: string;
}

export interface TransactionSentEvent {
  transactionId: string;
  reference: string;
  amount: number;
  fee: number;
  totalAmount: number;
  currency: string;
  recipientName: string;
  description?: string;
  createdAt: string;
  timestamp: string;
}

// Notification icon/color config helpers
export const NOTIFICATION_CONFIG: Record<
  string,
  { icon: string; color: string; bg: string }
> = {
  AGENT_APPROVED:            { icon: '✅', color: 'text-green-700',  bg: 'bg-green-50'  },
  AGENT_REJECTED:            { icon: '❌', color: 'text-red-700',    bg: 'bg-red-50'    },
  AGENT_SUSPENDED:           { icon: '⛔', color: 'text-orange-700', bg: 'bg-orange-50' },
  AGENT_ACTIVATED:           { icon: '🔓', color: 'text-green-700',  bg: 'bg-green-50'  },
  AGENT_REGISTERED:          { icon: '📋', color: 'text-blue-700',   bg: 'bg-blue-50'   },
  COMMISSION_EARNED:         { icon: '💸', color: 'text-purple-700', bg: 'bg-purple-50' },
  COMMISSIONS_SETTLED:       { icon: '💰', color: 'text-green-700',  bg: 'bg-green-50'  },
  FLOAT_LOW:                 { icon: '⚠️', color: 'text-yellow-700', bg: 'bg-yellow-50' },
  FLOAT_CRITICAL:            { icon: '🚨', color: 'text-orange-700', bg: 'bg-orange-50' },
  FLOAT_DEPLETED:            { icon: '❌', color: 'text-red-700',    bg: 'bg-red-50'    },
  FLOAT_REPLENISHED:         { icon: '✅', color: 'text-green-700',  bg: 'bg-green-50'  },
  FLOAT_RECHARGE_REJECTED:   { icon: '❌', color: 'text-red-700',    bg: 'bg-red-50'    },
  FLOAT_RECHARGE_REQUESTED:  { icon: '💰', color: 'text-blue-700',   bg: 'bg-blue-50'   },
  'float:recharge_approved': { icon: '✅', color: 'text-green-700',  bg: 'bg-green-50'  },
  'float:recharge_rejected': { icon: '❌', color: 'text-red-700',    bg: 'bg-red-50'    },
  CASH_IN_COMPLETED:         { icon: '💵', color: 'text-green-700',  bg: 'bg-green-50'  },
  CASH_OUT_COMPLETED:        { icon: '💵', color: 'text-blue-700',   bg: 'bg-blue-50'   },
  CASH_OUT_INITIATED:        { icon: '🔄', color: 'text-blue-700',   bg: 'bg-blue-50'   },
  DAILY_REPORT_GENERATED:    { icon: '📊', color: 'text-blue-700',   bg: 'bg-blue-50'   },
  TRANSACTION_RECEIVED:      { icon: '📥', color: 'text-green-700',  bg: 'bg-green-50'  },
  TRANSACTION_SENT:          { icon: '📤', color: 'text-gray-700',   bg: 'bg-gray-50'   },
  KYC_APPROVED:              { icon: '🛡️', color: 'text-green-700',  bg: 'bg-green-50'  },
  KYC_REJECTED:              { icon: '🛡️', color: 'text-red-700',    bg: 'bg-red-50'    },
  SECURITY_ALERT:            { icon: '🔒', color: 'text-red-700',    bg: 'bg-red-50'    },
  SYSTEM_MAINTENANCE:        { icon: '🔧', color: 'text-gray-700',   bg: 'bg-gray-50'   },
  SYSTEM_UPDATE:             { icon: 'ℹ️', color: 'text-blue-700',   bg: 'bg-blue-50'   },
  DEFAULT:                   { icon: '🔔', color: 'text-gray-700',   bg: 'bg-gray-50'   },
};

export function getNotificationConfig(type: string) {
  return NOTIFICATION_CONFIG[type] ?? NOTIFICATION_CONFIG.DEFAULT;
}
